import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Phone, 
  Award, 
  ChevronDown
} from 'lucide-react';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work1.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* Large Desktop Screens (1440px+) */
  @media (min-width: 1440px) {
    height: 100vh;
    padding: 0 4rem;
  }

  /* Desktop Screens (1200px - 1439px) */
  @media (min-width: 1200px) and (max-width: 1439px) {
    height: 100vh;
    padding: 0 3rem;
  }

  /* Laptop Screens (1024px - 1199px) */
  @media (min-width: 1024px) and (max-width: 1199px) {
    height: 100vh;
    padding: 0 2.5rem;
  }

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Large Desktop Screens (1440px+) */
  @media (min-width: 1440px) {
    max-width: 1200px;
    padding: 0 2rem;
  }

  /* Desktop Screens (1200px - 1439px) */
  @media (min-width: 1200px) and (max-width: 1439px) {
    max-width: 1100px;
    padding: 0 1.5rem;
  }

  /* Laptop Screens (1024px - 1199px) */
  @media (min-width: 1024px) and (max-width: 1199px) {
    max-width: 1000px;
    padding: 0 1.25rem;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
    max-width: 100%;
  }

  @media (max-width: 389px) {
    padding: 0 0.25rem;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;

  svg {
    color: #FECE1B;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
  }

  @media (max-width: 389px) {
    padding: 0.4rem 0.75rem;
    margin-bottom: 1rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.3rem 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.7rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  /* Large Desktop Screens (1440px+) */
  @media (min-width: 1440px) {
    font-size: clamp(3.5rem, 4vw, 5.5rem);
    margin-bottom: 2rem;
    line-height: 1.05;
  }

  /* Desktop Screens (1200px - 1439px) */
  @media (min-width: 1200px) and (max-width: 1439px) {
    font-size: clamp(3rem, 4.5vw, 5rem);
    margin-bottom: 1.75rem;
    line-height: 1.08;
  }

  /* Laptop Screens (1024px - 1199px) */
  @media (min-width: 1024px) and (max-width: 1199px) {
    font-size: clamp(2.5rem, 5vw, 4.5rem);
    margin-bottom: 1.5rem;
    line-height: 1.1;
  }

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(1.875rem, 5.25vw, 3rem);
    margin-bottom: 1.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: 2.5rem;
  opacity: 0.95;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Large Desktop Screens (1440px+) */
  @media (min-width: 1440px) {
    font-size: clamp(1.25rem, 2vw, 1.75rem);
    margin-bottom: 3rem;
    line-height: 1.5;
    max-width: 900px;
  }

  /* Desktop Screens (1200px - 1439px) */
  @media (min-width: 1200px) and (max-width: 1439px) {
    font-size: clamp(1.125rem, 2.25vw, 1.625rem);
    margin-bottom: 2.75rem;
    line-height: 1.55;
    max-width: 800px;
  }

  /* Laptop Screens (1024px - 1199px) */
  @media (min-width: 1024px) and (max-width: 1199px) {
    font-size: clamp(1rem, 2.5vw, 1.5rem);
    margin-bottom: 2.5rem;
    line-height: 1.6;
    max-width: 750px;
  }

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(0.875rem, 2.25vw, 1.125rem);
    margin-bottom: 1.5rem;
    line-height: 1.5;
    max-width: 100%;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(0.9375rem, 2.375vw, 1.25rem);
    margin-bottom: 2rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  /* Large Desktop Screens (1440px+) */
  @media (min-width: 1440px) {
    gap: 2rem;
    margin-bottom: 4rem;
  }

  /* Desktop Screens (1200px - 1439px) */
  @media (min-width: 1200px) and (max-width: 1439px) {
    gap: 1.75rem;
    margin-bottom: 3.5rem;
  }

  /* Laptop Screens (1024px - 1199px) */
  @media (min-width: 1024px) and (max-width: 1199px) {
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 389px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Large Desktop Screens (1440px+) */
  @media (min-width: 1440px) {
    padding: 1.5rem 3rem;
    font-size: 1.25rem;
    gap: 1rem;
  }

  /* Desktop Screens (1200px - 1439px) */
  @media (min-width: 1200px) and (max-width: 1439px) {
    padding: 1.375rem 2.75rem;
    font-size: 1.15rem;
    gap: 0.875rem;
  }

  /* Laptop Screens (1024px - 1199px) */
  @media (min-width: 1024px) and (max-width: 1199px) {
    padding: 1.25rem 2.5rem;
    font-size: 1.1rem;
    gap: 0.75rem;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for desktop */
  @media (min-width: 1440px) {
    svg {
      width: 24px;
      height: 24px;
    }
  }

  @media (min-width: 1200px) and (max-width: 1439px) {
    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (min-width: 1024px) and (max-width: 1199px) {
    svg {
      width: 20px;
      height: 20px;
    }
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%, rgba(255, 255, 255, 0.1) 100%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 20px 50px rgba(254, 206, 27, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.2);
      animation: pulse 2s infinite;
    }

    &:hover::after {
      opacity: 1;
    }

    &:active {
      transform: translateY(-1px) scale(0.98);
    }

    @keyframes pulse {
      0%, 100% { 
        box-shadow: 0 20px 50px rgba(254, 206, 27, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.2);
      }
      50% { 
        box-shadow: 0 25px 60px rgba(254, 206, 27, 0.8), 0 0 0 2px rgba(255, 255, 255, 0.3);
      }
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }

  &.quote-button {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 
      0 8px 32px rgba(254, 206, 27, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    border: none;
    position: relative;
    overflow: hidden;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-size: 1.1rem;
    border-radius: 50px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%);
      border-radius: 50px;
      pointer-events: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      transition: left 0.8s ease;
      border-radius: 50px;
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-3px) scale(1.03);
      box-shadow: 
        0 12px 40px rgba(254, 206, 27, 0.6),
        0 0 0 1px rgba(255, 255, 255, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.15);
    }

    &:hover::after {
      left: 100%;
    }

    &:active {
      transform: translateY(-1px) scale(1.01);
      box-shadow: 
        0 6px 20px rgba(254, 206, 27, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.25);
    }

    svg {
      transition: transform 0.3s ease;
      z-index: 2;
      position: relative;
    }

    &:hover svg {
      transform: translateX(4px);
    }

    /* Mobile adjustments for quote button */
    @media (max-width: 768px) {
      font-size: 1rem;
      letter-spacing: 0.3px;
    }

    @media (max-width: 389px) {
      font-size: 0.9rem;
      letter-spacing: 0.2px;
    }
  }

`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.4rem 0.6rem;
    gap: 0.25rem;
    
    span {
      font-size: 0.75rem;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 427px) and (min-width: 390px) {
    bottom: 1.25rem;
    padding: 0.35rem 0.5rem;
    
    span {
      font-size: 0.7rem;
    }
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: 389px) {
    bottom: 1rem;
    padding: 0.3rem 0.45rem;
    gap: 0.2rem;
    
    span {
      font-size: 0.65rem;
    }
    
    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    bottom: 0.75rem;
    padding: 0.25rem 0.4rem;
    gap: 0.15rem;
    
    span {
      font-size: 0.6rem;
    }
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

interface HeroSectionProps {
  title: string;
  subtitle: string;
  badge?: string;
  showScrollIndicator?: boolean;
  backgroundImage?: string;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

const HeroSectionComponent: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  badge = "500+ Projects Completed • 10+ Year Warranty",
  showScrollIndicator = true,
  backgroundImage = '/images/resin-surfaces-bound-work1.jpeg',
  primaryButtonText = "Get Free Quote",
  primaryButtonLink = "/contact/",
  secondaryButtonText = "Call Now",
  secondaryButtonLink = "tel:+447535935779"
}) => {
  const handleScrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <HeroSection style={{ backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('${backgroundImage}')` }}>
      <HeroContent>
        <HeroBadge
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Award size={16} />
          <span>{badge}</span>
        </HeroBadge>
        
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {title}
        </HeroTitle>
        
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {subtitle}
        </HeroSubtitle>
        
        <CTAButtons
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <StyledButton to={primaryButtonLink} className="quote-button">
            {primaryButtonText} <ArrowRight size={20} />
          </StyledButton>
          <StyledButton to={secondaryButtonLink} className="secondary">
            <Phone size={20} /> {secondaryButtonText}
          </StyledButton>
        </CTAButtons>
      </HeroContent>
      
      {showScrollIndicator && (
        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          onClick={handleScrollToContent}
        >
          <span>Discover More</span>
          <ChevronDown size={24} />
        </ScrollIndicator>
      )}
    </HeroSection>
  );
};

export default HeroSectionComponent;
